import React, { useState, useEffect, useRef } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { httpRequest } from "../../services/http";
import LoginModal from "../../components/LoginModal";
import Modal from "../../components/atoms/Modal";
import isUserLoggedIn from "../../common/helpers/isUserLoggedIn";
import { eventTrack } from "../../common/helpers/helpers";
import { ANALYTICS_CATEGORIES } from "../../components/cards/common/constants";
import {
  getVarsomeHost,
  getQueryType,
  getQuery,
} from "../../state/selectors/general.selectors";

const FavoriteButton = ({ shortUrl, varsomeHost, currentQuery, queryType }) => {
  const [favoriteLoaded, setFavoriteLoaded] = useState(false);
  const [favorite, setFavorite] = useState(false);
  const [notes, setNotes] = useState("");
  const [savedNotes, setSavedNotes] = useState("");
  const [notesModalOpen, setNotesModalOpen] = useState(false);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [edit, setEdit] = useState(false);
  const pendingToggleRequestController = useRef(null);
  const pendingSaveRequestController = useRef(null);

  useEffect(() => {
    const getFavorite = async () => {
      if (!isUserLoggedIn()) {
        setFavoriteLoaded(true);
        setNotes("");
        return;
      }

      try {
        const url = `${varsomeHost}/favorites/favorite?short_url=${encodeURIComponent(
          shortUrl
        )}`;

        const response = await httpRequest.get(url);

        const { data } = response;
        const fetchedNotes = data ? data.notes : null;

        setFavoriteLoaded(true);
        setFavorite(true);
        setNotes(fetchedNotes);
      } catch (error) {
        console.error("Error getting favorite:", error);
        setFavoriteLoaded(true);
        setFavorite(false);
        setNotes("");
      }
    };

    getFavorite();
  }, [shortUrl]);

  const removeFavorite = () => {
    if (pendingToggleRequestController.current) {
      pendingToggleRequestController.current.abort();
    }

    const url = `${varsomeHost}/favorites/favorite?short_url=${encodeURIComponent(
      shortUrl
    )}`;

    pendingToggleRequestController.current = new AbortController();
    setFavorite(false);

    httpRequest
      .delete(url, { signal: pendingToggleRequestController.current.signal })
      .then(() => {
        setEdit(false);
        setNotes("");
      })
      .catch((error) => {
        console.error("While removing favorite etc.):", error);
        setFavorite(true);
      })
      .finally(() => {
        pendingToggleRequestController.current = null;
      });
  };

  const saveFavorite = () => {
    if (pendingSaveRequestController.current) {
      pendingSaveRequestController.current.abort();
    }

    const url = `${varsomeHost}/favorites/favorite?short_url=${encodeURIComponent(
      shortUrl
    )}`;
    let requestData = { notes };

    if (!edit) {
      requestData = {
        ...requestData,
        query_type: queryType === "gene" ? "gene" : "variant",
        query: currentQuery,
      };
    }

    setFavorite(true);
    setNotesModalOpen(false);
    setSavedNotes(notes);

    pendingSaveRequestController.current = new AbortController();

    httpRequest({
      method: edit ? "put" : "post",
      url,
      data: requestData,
      signal: pendingSaveRequestController.current.signal,
    })
      .then(() => {})
      .catch((error) => {
        console.error("While saving favorite etc.):", error);
        setFavorite(!edit);
      })
      .finally(() => {
        pendingSaveRequestController.current = null;
      });
  };

  const toggleFavorite = () => {
    eventTrack(
      ANALYTICS_CATEGORIES.ANALYSIS_PAGE_HEADER_BUTTONS,
      "View Favourite",
      ""
    );

    if (favorite) {
      removeFavorite();
    } else {
      setNotesModalOpen(true);
    }
  };

  const toggleLoginModal = () => {
    setShowLoginModal(!showLoginModal);
  };

  const clickEvent = isUserLoggedIn() ? toggleFavorite : toggleLoginModal;

  const helpTourText = `You can add the current variant or gene to your favorites and save your personal notes on it for future reference.`;
  const tipTextRemove = "Remove this variant or gene from your favorites.";
  const tipTextAdd = "Add this variant or gene to your favorites.";
  const tipTextEdit = "View and edit notes for this favorite.";
  const buttonTitle = " Favorites";

  return (
    <span data-testid="favorite-loaded">
      <span
        className="saph-combo-button1"
        style={favoriteLoaded ? {} : { display: "none" }}
      >
        <div
          className="saph-button saph-white-ghost"
          data-helptour={helpTourText}
          data-helppos="3"
          data-tooltip={favorite ? tipTextRemove : tipTextAdd}
          onClick={clickEvent}
        >
          <i
            className={favorite ? "tw-text-primary fa fa-star" : "fa fa-star-o"}
          />
          {buttonTitle}
        </div>
        <div
          className="saph-button saph-white-ghost tw-font-bold"
          data-tooltip={tipTextEdit}
          onClick={() => {
            eventTrack(
              ANALYTICS_CATEGORIES.ANALYSIS_PAGE_HEADER_BUTTONS,
              "Edit Notes",
              ""
            );
            setNotesModalOpen(true);
            setEdit(true);
          }}
          style={favorite ? null : { display: "none" }}
        >
          Notes
        </div>
      </span>
      <span data-testid="add-to-favorites">
        <Modal
          isOpen={notesModalOpen}
          onRequestClose={() => {
            setNotes(favorite ? savedNotes : "");
            setNotesModalOpen(false);
            setEdit(false);
          }}
          ContentRender={() => {
            return (
              <div className="tw-max-w-90screen tw-min-w-80 tw-p-6">
                <h4>Add to favorites</h4>
                <p>
                  You may optionally add notes for this variant. You can access
                  these notes at any time by viewing this variant or through
                  your favorites list.
                </p>
                <textarea
                  data-testid="notes-controls"
                  className="form-control"
                  name="favorite_notes"
                  required={true}
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Add notes..."
                  rows="5"
                  autoFocus={true}
                />
                <div className="varsome-clearfix tw-mt-8">
                  <button
                    type="button"
                    className="btn varsome-left cancel-button"
                    onClick={() => {
                      setNotes(favorite ? savedNotes : "");
                      setNotesModalOpen(false);
                      setEdit(false);
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-success varsome-right"
                    onClick={saveFavorite}
                    data-testid="notes-saved"
                  >
                    Save
                  </button>
                </div>
              </div>
            );
          }}
        />
      </span>

      <LoginModal showModal={showLoginModal} toggleModal={toggleLoginModal} />
    </span>
  );
};

const mapStateToProps = (state) => {
  return {
    varsomeHost: getVarsomeHost(state),
    queryType: getQueryType(state),
    currentQuery: getQuery(state),
  };
};

FavoriteButton.propTypes = {
  shortUrl: PropTypes.string.isRequired,
  varsomeHost: PropTypes.string,
  queryType: PropTypes.string,
  currentQuery: PropTypes.string,
};

FavoriteButton.defaultProps = {
  varsomeHost: "",
  queryType: "",
  currentQuery: "",
};

export default connect(mapStateToProps, {})(FavoriteButton);
